import { gsap } from "gsap";
import { type RefObject, useLayoutEffect } from "react";

import { usePrefersReducedMotion } from "@/shared/hooks/usePrefersReducedMotion";

import { MOTION_DURATION, MOTION_EASE, MOTION_RISE, MOTION_STAGGER } from "./motion.tokens";

/**
 * Fades up the `[data-reveal]` descendants of `ref` once, staggered, when the container mounts.
 *
 * GSAP sets the from-state, CSS sets none: if the script never runs, or motion is reduced, every element is
 * simply there. The failure mode is "no animation", never "invisible". Layout effect, so the from-state lands
 * before the first paint and nothing flashes in its final place first.
 */
export function useRevealOnMount(
  ref: RefObject<HTMLElement | null>,
  options: { stagger?: number; delay?: number; deps?: readonly unknown[] } = {},
): void {
  const reduced = usePrefersReducedMotion();
  const { stagger = MOTION_STAGGER.base, delay = 0, deps = [] } = options;

  useLayoutEffect(() => {
    const root = ref.current;
    if (!root || reduced) return;
    const ctx = gsap.context(() => {
      const items = root.querySelectorAll<HTMLElement>("[data-reveal]");
      if (!items.length) return;
      gsap.fromTo(
        items,
        { opacity: 0, y: MOTION_RISE },
        {
          opacity: 1,
          y: 0,
          duration: MOTION_DURATION.enter,
          ease: MOTION_EASE.enter,
          stagger,
          delay,
          clearProps: "transform,opacity",
        },
      );
    }, root);
    return () => ctx.revert();
    // `deps` replays the entrance when the content it describes changes (a new step).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduced, stagger, delay, ...deps]);
}
